'use client';

/**
 * Call mode hook — manages live voice call with AudioWorklet streaming.
 *
 * When a call is active, captures mono audio from the microphone at the
 * DEVICE'S NATIVE sample rate via an AudioWorkletNode, accumulates ~100ms of
 * frames, resamples to the backend's 24kHz PCM16 contract (see
 * lib/audio-resample.ts for why capture must not run at a forced 24kHz),
 * and sends as JSON via WebSocket.
 *
 * Audio chain: MediaStream → AnalyserNode (viz) + AudioWorkletNode (capture) → WS send
 *
 * This hook owns the AnalyserNode's lifecycle (create/connect/disconnect)
 * and hands it to the audio level engine via `registerUserAnalyser` — the
 * engine's single rAF loop (see useAudioVisualization.ts) reads it from
 * there. There is no local analysis loop here anymore.
 *
 * This hook does NOT own Sara's in-flight call transcript. That used to
 * live here as a separate `liveUtterance` model (FB22); it's gone — voice
 * streaming now rides the SAME message-store pipeline text mode uses
 * (`appendAssistantDelta` in useMessageManagement, wired from
 * useAssistantConnection's TRANSCRIPT_DELTA handler), so a call's partial
 * words are durable in `messages` the instant they stream, with nothing
 * call-mode-specific to commit on hang-up.
 *
 * [FB34] Optimistic call-start UI + latency overlap. `isCalling`/
 * `isCallingRef` KEEP their pre-FB34 internal semantics unchanged (they gate
 * the capture pump, the mic-silence watchdog, and rtc-recovery's arming) and
 * still flip at the SAME point they always have — right after the OpenAI
 * call answers, before ICE has necessarily finished connecting. The NEW
 * `callPhase` (src/lib/call-phase.ts) is the external-facing signal every
 * consumer OUTSIDE this hook should read instead: the call surface mounts
 * the instant a call is tapped (`connecting`), well before `isCalling` ever
 * flips, and stays in `connecting` until the transport's own definition of
 * "live" is met (ws: capture ready; rtc: pc connected AND mic track
 * attached, independently). See call-phase.ts's header for the full
 * rationale and src/hooks/useCallMode.test.ts for the transition coverage.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { encodePcm16Chunk, peakAmplitude } from '@/lib/audio-resample';
import {
  clearPreferredMicId,
  rankMicCandidates,
  readPreferredMicId,
  storePreferredMicId,
} from '@/lib/mic-selection';
import {
  CallNoticeKind,
  CallNoticeTopic,
  WSEventType,
  type CallNotice,
} from '@/types/chat';
import {
  modelBusyNotice,
  nextModelBusyState,
  patienceNotice,
  retractModelBusyNotice,
  retractSilenceWarning,
  type ModelBusyState,
} from '@/lib/call-notice';
import { createHiddenAudioSink } from '@/lib/audio-sink';
// [rtc-spike] See src/lib/rtc-call.ts's header for the transport this
// enables by default; ?rtc=0 forces the legacy WS path below.
import {
  RtcCallTimeoutError,
  canOverlapCaptureWithCall,
  startRtcCall,
  startRtcCallOffer,
  stopRtcCall,
} from '@/lib/rtc-call';
// [SDP-over-WS] classifyStartFailure below needs to recognize an explicit
// rtc_call_failed rejection — see that function's doc comment.
import { RtcWsNegotiateError } from '@/lib/rtc-ws-negotiate';
// [rtc-reliability] Pure connection-recovery state machine — see that
// file's header for the state model useCallMode drives below.
import {
  GRACE_MS,
  initialRtcRecoveryState,
  rtcRecoveryPresentation,
  rtcRecoveryReducer,
  type RtcRecoveryEvent,
  type RtcRecoveryState,
} from '@/lib/rtc-recovery';
// [FB34] Pure call-start-phase state machine — see that file's header for
// the full rationale (isCalling vs callPhase, transport-gated `live`).
import {
  callPhaseReducer,
  initialCallPhaseState,
  type CallFailureReason,
  type CallPhase,
  type CallPhaseEvent,
  type CallPhaseState,
  type CallTransport,
} from '@/lib/call-phase';
import { getAssistantApiUrl } from '@/lib/assistant-config';
import { getDeviceToken } from '@/lib/device-token';

/** SUCCESS notices auto-clear after this long — matches the 3.4s
 *  `call-notice-dismiss` hairline animation in tailwind.config.ts. */
const SUCCESS_NOTICE_MS = 3400;

/** Turn 15's MODEL_BUSY recovery snapshot ("the exhale", 15e) survives this
 *  long past the state clear — matches `call-notice-release`'s duration in
 *  tailwind.config.ts. See handleModelResponded. */
const NOTICE_RELEASE_MS = 300;

// [rtc-reliability] OpenAI hard-kills realtime sessions at 60 minutes with
// no warning of its own — these are the client-side warn/end timers that
// fill that gap. rtc-only (see their registration in startCall's rtc
// branch): the WS transport has no such cap via our relay. Module-level
// consts (not inline literals), exported so tests import and assert against
// the exact same values rather than a hardcoded magic number that could
// drift out of sync.
export const CALL_LIMIT_WARN_MS = 55 * 60 * 1000;
export const CALL_LIMIT_END_MS = 58 * 60 * 1000;

// [FB34] getUserMedia has no timeout of its own — an ignored permission
// prompt would leave `connecting` showing the same "Connecting…" copy
// forever. After this long, the driver swaps to permission-aware copy
// (waitingOnMicPermission below) WITHOUT auto-failing the call — the user
// may still grant it at any point. See call-phase.ts's
// START_CONNECTING_WATCHDOG/CANCEL_CONNECTING_WATCHDOG effects.
export const CONNECTING_WATCHDOG_MS = 10_000;

const CHUNK_SECONDS = 0.1; // accumulate ~100ms of capture before each send

/**
 * Silence policy for the capture pump. All *_CHUNKS values are in
 * CHUNK_SECONDS-sized chunks (0.1s each), so e.g. 30 chunks ≈ 3s.
 *
 * The detection ladder, quietest to loudest:
 *  - SILENCE_WATCHDOG_CHUNKS + peak < SILENCE_PEAK_THRESHOLD (~3s): a dead
 *    capture device — exact digital zeros (macOS lid-closed built-in mic,
 *    observed live) or a faint sub-noise-floor hiss that never registers
 *    real audio. Advisory only (FB28): detection used to have a ~0.5s
 *    exact-zero fast stage that auto-switched devices, but Bluetooth
 *    hands-free mics deliver 1–3s of exact zeros while the headset
 *    renegotiates A2DP→HFP at capture start, and the fast stage
 *    false-positived on that warmup — switching capture AWAY from the
 *    device the user hears Sara through (car speakers + phone mic = echo
 *    the browser's AEC can't cancel across devices, and Sara transcribed
 *    her own words as user speech). With nothing to auto-switch, 3s of
 *    patience costs only notice latency and clears the warmup window.
 *  - SPEECH_PEAK_THRESHOLD + NUDGE_CHUNKS (~15s): the USER is being heard.
 *    A mic can idle above the noise floor forever on ambient sound while
 *    the speaker's voice goes to a different device — no watchdog fires,
 *    so the once-per-call "Sara can't hear you" nudge covers that gap.
 */
const MIC_WATCHDOG_POLICY = {
  SILENCE_WATCHDOG_CHUNKS: 30, // ~3s of capture
  SILENCE_PEAK_THRESHOLD: 1e-4, // true zeros; room noise is >~1e-3
  SPEECH_PEAK_THRESHOLD: 0.02, // speech-level peaks
  NUDGE_CHUNKS: 150, // ~15s of capture
} as const;

// Shared by the normal AudioWorklet capture path AND the [rtc-spike] path
// below — same mic, same constraints, regardless of transport. `deviceId`
// stays out of this const because it varies per call (remembered device,
// explicit switch, or none) — see buildCapture's getUserMedia call.
const MIC_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
};

// [rtc-spike] Query flag for the WebRTC transport — see src/lib/rtc-call.ts.
// WebRTC is now the DEFAULT transport; ?rtc=0 (or ?rtc=false) is the escape
// hatch back to the legacy WS path. Kept as a single named constant so the
// literal string only appears once.
const RTC_QUERY_FLAG = 'rtc';

/** [rtc-spike] Pure parse of RTC_QUERY_FLAG, extracted so it's unit-testable
 *  without a `window`. Enabled by default: only an explicit `0`/`false`
 *  value disables it — absent, `1`, `true`, empty, or any unrelated query
 *  param all resolve to enabled. */
export function resolveRtcEnabled(search: string): boolean {
  const value = new URLSearchParams(search).get(RTC_QUERY_FLAG);
  return value !== '0' && value !== 'false';
}

/** [rtc-spike] SSR-safe: `window` is undefined during server rendering, so
 *  this resolves to the same default (true) resolveRtcEnabled returns for a
 *  missing flag. A plain `window.location.search` read (same pattern
 *  AuthContext.tsx already uses for the current URL) rather than
 *  next/navigation's `useSearchParams` — this is a module-level helper, not
 *  a component hook. In practice this is only ever read inside startCall,
 *  which only runs from a user gesture (always client-side) — the SSR
 *  branch below is a defensive default, not a path prerendering actually
 *  exercises. */
function isRtcFlagEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  return resolveRtcEnabled(window.location.search);
}

/** [FB34] Maps a startCall/retryCall failure to one of call-phase.ts's fixed
 *  reasons. `NotFoundError`/`OverconstrainedError` (no mic device at all)
 *  collapse into `mic_denied` alongside `NotAllowedError`/`SecurityError` —
 *  the fixed reason enum has no distinct "no device" bucket, and both read
 *  to the user as "Sara can't hear you because of a mic problem". Anything
 *  else (a generic Error, e.g. buildCapture returning zero audio tracks) is
 *  `backend` — an unexpected condition, not a permission one. */
export function classifyStartFailure(err: unknown): CallFailureReason {
  if (err instanceof RtcCallTimeoutError) return 'timeout';
  if (err instanceof RtcWsNegotiateError) {
    // Explicit backend rtc_call_failed — err.reason is the backend's
    // RtcCallFailureReason ('upstream_error' | 'attach_failed'). Both map to
    // 'backend' for NOW because the failure-card UX doesn't differentiate
    // yet, but the reason is still logged (rather than silently discarded)
    // since this is the only place that ever sees it: if the UX starts
    // differentiating, 'upstream_error' is the one that's plausibly
    // retryable (OpenAI itself declined the offer, no backend-side call
    // was ever created) vs. 'attach_failed' (a call WAS created upstream
    // but this backend's own sideband wiring to it broke).
    console.warn('[rtc-ws] call failed:', err.reason);
    return 'backend';
  }
  const name = err instanceof DOMException ? err.name : '';
  if (
    name === 'NotAllowedError' ||
    name === 'SecurityError' ||
    name === 'NotFoundError' ||
    name === 'OverconstrainedError'
  ) {
    return 'mic_denied';
  }
  return 'backend';
}

/**
 * [SDP-over-WS] Builds the `wsSend` hint rtc-call.ts's negotiate()/
 * stopRtcCall() use to choose their transport: present ⇔ the assistant
 * socket is OPEN right at the call site (checked fresh each time — this
 * hook already sends audio chunks and one-shot control frames, like
 * RETRY_RESPONSE below, the same way, via `wsRef.current?.readyState ===
 * WebSocket.OPEN`). Undefined means "no open socket" — rtc-call.ts falls
 * back to its original REST path unchanged, exactly as if this feature
 * didn't exist.
 */
function rtcWsSend(
  socket: WebSocket | null
): ((json: string) => void) | undefined {
  return socket && socket.readyState === WebSocket.OPEN
    ? (json: string) => socket.send(json)
    : undefined;
}

// ─── Hook ───────────────────────────────────────────────────────

interface UseCallModeOptions {
  wsRef: React.MutableRefObject<WebSocket | null>;
  /** Hands the freshly-created (or, on stop, null) user AnalyserNode to the
   *  audio level engine. See useAudioVisualization.ts. */
  registerUserAnalyser: (analyser: AnalyserNode | null) => void;
  /** Snaps VizMode/levels back to IDLE immediately on stop, instead of
   *  waiting out the engine's natural activity-timeout decay. */
  resetToIdle: () => void;
  /** Drops the agent playback queue (useAudioPlayback.clearPlayback).
   *  Called inside stopCall so the invariant "ending a call silences Sara"
   *  holds on EVERY stop path — incoming AUDIO_DATA is already gated on
   *  isCallingRef, so without this the locally-buffered tail of her answer
   *  kept playing after hang-up. */
  clearPlayback: () => void;
  /** Builds + unlocks the agent playback chain (useAudioPlayback.
   *  primePlayback) inside the call-start gesture. The playback path ends
   *  in an <audio> element (the echo-cancellation reference — see
   *  useAudioPlayback's header), and media elements are autoplay-gated:
   *  built lazily on the first audio chunk they can come up blocked and
   *  Sara plays silently. Same gesture-time trick as gestureCtx below. */
  primePlayback: () => void;
  /** [rtc-spike] Current WS session id, mirrored from the conversation
   *  store (see ChatContext.tsx's `sessionIdRef` — the SAME ref
   *  useAssistantConnection.ts reads to build the `/ws/{session_id}` URL).
   *  The rtc path sends this to our backend's `/api/rtc/*` endpoints so it
   *  can correlate the WebRTC call with the same session. */
  sessionIdRef: React.MutableRefObject<string>;
  /** [rtc-spike] Hands the freshly-created (or, on teardown, null) AGENT
   *  AnalyserNode to the audio level engine — the SAME function
   *  useAudioPlayback receives for the normal (WS) path's agent audio (see
   *  ChatContext.tsx). Passing it to both is safe because only one
   *  transport ever produces agent audio for a given call: the WS path
   *  never touches this option, and the rtc path never calls
   *  useAudioPlayback's enqueueAudio. */
  registerAgentAnalyser: (analyser: AnalyserNode | null) => void;
}

interface BuildCaptureOptions {
  deviceId?: string;
  /** Non-exact ("ideal") for the REMEMBERED device at call start — a
   *  missing/unplugged device silently falls back to the browser default
   *  instead of throwing OverconstrainedError. Exact for explicit
   *  user-switch attempts, where a specific device was chosen and falling
   *  back silently to something else would be the wrong behavior. */
  exactDevice?: boolean;
  /** Context pre-created synchronously inside the user gesture (initial
   *  attempt only) — see the comment at the call site. */
  gestureCtx?: AudioContext;
  /** Checked after every await inside buildCapture — true means a
   *  stop/switch/newer-generation build raced this one and it must tear
   *  itself down instead of assigning into state another attempt now owns. */
  aborted: () => boolean;
  onSilent: (silentTrack: MediaStreamTrack | undefined) => void;
}

export function useCallMode({
  wsRef,
  registerUserAnalyser,
  resetToIdle,
  clearPlayback,
  primePlayback,
  sessionIdRef,
  registerAgentAnalyser,
}: UseCallModeOptions) {
  const [isCalling, setIsCalling] = useState(false);
  const isCallingRef = useRef(false);
  const callAudioContextRef = useRef<AudioContext | null>(null);
  const callAnalyserRef = useRef<AnalyserNode | null>(null);
  const callProcessorRef = useRef<AudioWorkletNode | null>(null);
  const callStreamRef = useRef<MediaStream | null>(null);
  const callBufferRef = useRef<Float32Array[]>([]);
  // Native sample rate of the ACTIVE call's capture context — stopCall's tail
  // flush must resample from the same rate the worklet frames were captured at.
  const callInputRateRef = useRef<number>(24000);
  const callStartedAtRef = useRef<number | null>(null);
  // Bumped on every start/stop/switch. startCall/switchMic capture its value
  // and re-check it after each await; if it changed (a stopCall / WS-close /
  // newer switch raced the async build), the build aborts and tears down the
  // resources it created locally instead of assigning an orphaned mic/
  // AudioContext nothing owns.
  const callGenerationRef = useRef(0);
  // True while THIS call has an outstanding "Connection lost" notice — the
  // restored-side handler only announces "back online" when a drop was
  // actually surfaced (a quick flap resolved between renders stays silent).
  const connectionLostRef = useRef(false);
  // The devicechange listener registered after a successful call start, so
  // stopCall can remove the exact function reference.
  const deviceChangeHandlerRef = useRef<(() => void) | null>(null);
  // "Sara can't hear you" nudge — once per CALL, not per capture chain (a
  // nudge that re-fired after every mic-switch rebuild would nag). Reset at
  // the top of every start attempt.
  const nudgeShownRef = useRef(false);
  // The nudge notice's "Try another mic" action calls switchMic, but the pump
  // that fires the nudge is built inside buildCapture, which is declared
  // BEFORE switchMic — a direct reference would be a TDZ error. The ref is
  // kept current after switchMic's declaration and read only at click time.
  const switchMicRef = useRef<((deviceId: string) => Promise<void>) | null>(
    null
  );

  // [rtc-spike] Live only while an rtc call is up (the default transport;
  // ?rtc=0 forces the legacy WS path instead). rtcPcRef is also the
  // "which transport is this call using" flag checked by stopCall/switchMic.
  // The mic MediaStream itself is stored in the SAME callStreamRef the
  // normal path uses (see startCall's rtc branch) — that's what lets
  // toggleMute and stopCall's track-stop cleanup work for both transports
  // without their own rtc branches.
  //
  // [FB34] Assigned as soon as the SDP answer lands (ANSWER_RECEIVED),
  // not only once the call is fully established — see runRtcAttempt below.
  // That's what lets stopCall's own `if (rtcPc)` branch discover and hang
  // up a call the instant it exists, even while the OTHER half of the
  // latency-overlap pair (the mic capture arm) is still pending — e.g. an
  // unanswered permission prompt must never leave an orphaned backend call
  // waiting on a Promise that may not settle for a long time.
  const rtcPcRef = useRef<RTCPeerConnection | null>(null);
  // [rtc-reliability] The OpenAI call_id of the CURRENT live rtc call —
  // written wherever rtcPcRef is (startCall's rtc branch, performRtcRecall's
  // pc swap), nulled in teardownRtc. Every hangup POST carries it so the
  // backend can no-op instead of detaching a NEWER call the session has
  // since moved on to (a stale recall's cleanup or a tab-close race must
  // never kill a call the user legitimately restarted).
  const rtcCallIdRef = useRef<string | null>(null);
  const rtcSinkElementRef = useRef<HTMLAudioElement | null>(null);
  // [rtc-spike] Agent-level meter tap (Change 4) — a MediaStreamAudioSourceNode
  // + AnalyserNode built on the CAPTURE AudioContext when the remote track
  // arrives (see startCall's onRemoteStream). Never connected to a
  // destination; audible playback stays exclusively rtcSinkElementRef's job.
  // Torn down alongside the rest of the rtc chain in teardownRtc.
  const rtcRemoteSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const rtcRemoteAnalyserRef = useRef<AnalyserNode | null>(null);

  // [rtc-reliability] Pure state machine (src/lib/rtc-recovery.ts) tracking
  // THIS call's connection-drop recovery — grace window, one auto re-call,
  // terminal drop. A plain ref, not React state: every consumer either
  // fires from a pc event or reads it synchronously in the same tick, so
  // there's nothing for a re-render to reflect on its own — the
  // user-visible signal is the CallNotice this drives (see
  // dispatchRtcRecovery below). Reset to idle by teardownRtc. [FB34] Arms
  // only once callPhase reaches 'live' — see handleRtcConnectionStateChange.
  const rtcRecoveryRef = useRef<RtcRecoveryState>(initialRtcRecoveryState());
  // GRACE_MS timer handle for the DEGRADED status above.
  const rtcGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Forward-reference indirection — same TDZ workaround as switchMicRef
  // below: startCall (and performRtcRecall) must pass a STABLE
  // onConnectionStateChange callback into startRtcCall, but the real
  // handler is declared much later (after stopCall/startCall/showCallNotice
  // all exist for it to call). Set once, right after
  // handleRtcConnectionStateChange's own declaration.
  const handleRtcConnectionStateChangeRef = useRef<
    (state: RTCPeerConnectionState) => void
  >(() => {});
  // Mutual reference between dispatchRtcRecovery (drives performRtcRecall
  // via a START_RECALL effect) and performRtcRecall (reports back into
  // dispatchRtcRecovery once its async startRtcCall settles) — the same
  // kind of two-node cycle switchMicRef exists to break, just for this pair.
  const dispatchRtcRecoveryRef = useRef<(event: RtcRecoveryEvent) => void>(
    () => {}
  );
  // [rtc-reliability] A "Call again" chip built INSIDE a stopCall-adjacent
  // callback (endCallWithRetryChip) must NOT capture `startCall` by direct
  // closure: startCall's own reentrancy guard reads phase state from the
  // render `startCall` was memoized in, and stopCall's phase-idle dispatch
  // hasn't been committed to a NEW render yet at the point the chip's
  // onClick is being constructed (same synchronous tick) — so a captured
  // `startCall` would forever see the STALE (non-idle) phase and silently
  // no-op on every tap. Reading through a ref at CLICK time (a genuinely
  // later event, after React has re-rendered) always resolves to the
  // current, correct closure. Assigned right after startCall's own
  // declaration.
  const startCallRef = useRef<() => Promise<void>>(async () => {});
  // [rtc-reliability] Tab-close hangup — the pagehide listener registered
  // while an rtc call is live (see startCall's rtc branch); removed in
  // teardownRtc, same as the other rtc-only per-call resources.
  const pageHideHandlerRef = useRef<(() => void) | null>(null);
  // [rtc-reliability] 60-minute session cap timers — see CALL_LIMIT_WARN_MS/
  // CALL_LIMIT_END_MS above. Cleared in teardownRtc.
  const callLimitWarnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const callLimitEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // Forward-reference indirection (same idiom as startCallRef above) — the
  // 60-minute cap's END timer is armed inside runRtcAttempt, but the shared
  // "stopCall + Call again chip" helper it needs to invoke is declared much
  // later (it itself depends on stopCall/showCallNotice/startCallRef all
  // existing first). Assigned right after endCallWithRetryChip's own
  // declaration.
  const endCallWithRetryChipRef = useRef<
    (title: string, subtitle: string) => void
  >(() => {});

  // [rtc-spike] Which transport the CURRENT call is using. The pump (inside
  // buildCapture) gates its WS send on this instead of reading rtcPcRef
  // directly, because the RTCPeerConnection itself isn't created until
  // AFTER buildCapture resolves (see startCall) — a chunk arriving in that
  // window would still see rtcPcRef.current === null and ship over WS.
  // Flipped at the TOP of every start attempt, before buildCapture, so that
  // window is closed; flipped back to 'ws' in teardownRtc (covers both
  // stopCall's rtc branch and every start-attempt abort/failure path, which
  // all route through teardownRtc).
  const transportRef = useRef<'ws' | 'rtc'>('ws');

  // Default true (v3): the transcript rail is the primary call surface now,
  // so captions show by default. Not reset on start/stop — it's a session
  // preference (toggling it off mid-call shouldn't flip back on next call).
  const [showCC, setShowCC] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  // Mirror of isMuted for non-React consumers: a mic-switch rebuild (see
  // switchMic) replaces the MediaStream mid-call and must re-apply the
  // user's current mute to the fresh tracks.
  const isMutedRef = useRef(false);

  // Devices enumerated after permission is granted (labels only populate
  // post-grant) — feeds the mic-switcher UI. Refreshed on call start and on
  // every native 'devicechange' (plug/unplug) while a call is live.
  // `audioDevices` is the FULL list (inputs AND outputs — mic-selection's
  // headset classification needs the audiooutput groupIds); `audioInputs`
  // is the menu's row list, derived from it.
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const audioInputs = useMemo(
    () => audioDevices.filter((d) => d.kind === 'audioinput' && !!d.deviceId),
    [audioDevices]
  );
  const [activeMicId, setActiveMicId] = useState<string | null>(null);

  // In-call notification (Turn-12 12b) — rendered by voice/call-notice.tsx
  // inside the call surface. Replaces sonner toasts for IN-CALL mic events;
  // pre-call startup failures (permission denied, no mic) stay on sonner
  // because the call screen isn't mounted yet to host a notice.
  const [callNotice, setCallNoticeState] = useState<CallNotice | null>(null);
  // Mirror of callNotice, written every render (not just inside a callback)
  // so handleModelResponded can read the CURRENT value without a stale
  // closure — same "read current state directly" trick as switchMicRef
  // below, just for state instead of a function reference.
  const callNoticeRef = useRef<CallNotice | null>(null);
  callNoticeRef.current = callNotice;
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── [FB34] Call-phase state machine (src/lib/call-phase.ts) ──────
  //
  // Real React state (unlike rtcRecoveryRef above) — callPhase drives the
  // MOUNT of the call surface itself (page.tsx) and its connecting/failed
  // treatments, so it must trigger renders. callPhaseRef mirrors it for
  // synchronous reads inside callbacks/closures (toggleCall, stopCall,
  // handleRtcConnectionStateChange), same pattern as callNoticeRef above.
  const callPhaseRef = useRef<CallPhaseState>(initialCallPhaseState());
  const [callPhaseState, setCallPhaseState] = useState<CallPhaseState>(
    callPhaseRef.current
  );
  // getUserMedia has no timeout — see CONNECTING_WATCHDOG_MS above.
  const connectingWatchdogTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [waitingOnMicPermission, setWaitingOnMicPermission] = useState(false);
  // [FB34] Page-session mic memory feeding the latency-overlap gate (see
  // rtc-call.ts's canOverlapCaptureWithCall) — set on the FIRST successful
  // buildCapture this hook instance ever completes, in ANY transport/path.
  // Never reset by a later failure/stop: once the browser has proven it'll
  // hand over the mic on this page without a fresh prompt, that stays true
  // for the rest of the visit (iOS Safari re-prompts per PAGE LOAD, not per
  // call — see canOverlapCaptureWithCall's doc comment).
  const micGrantedThisSessionRef = useRef(false);
  // [FB34] `performance.now()` at the top of the CURRENT start attempt —
  // the origin every `[voice]` timing mark below measures from, so the
  // before/after latency win (tap → capture-ready → answer → connected →
  // track-attached) is directly readable from the console/next-dev.log
  // without cross-referencing timestamps by hand.
  const voiceTimingStartRef = useRef(0);
  const logVoiceTiming = useCallback((label: string) => {
    const elapsedMs = performance.now() - voiceTimingStartRef.current;
    console.info(`[voice] ${label} +${elapsedMs.toFixed(0)}ms`);
  }, []);

  const dispatchCallPhase = useCallback((event: CallPhaseEvent) => {
    const { state, effects } = callPhaseReducer(callPhaseRef.current, event);
    callPhaseRef.current = state;
    setCallPhaseState(state);
    for (const effect of effects) {
      switch (effect.type) {
        case 'START_CONNECTING_WATCHDOG':
          if (connectingWatchdogTimerRef.current) {
            clearTimeout(connectingWatchdogTimerRef.current);
          }
          setWaitingOnMicPermission(false);
          connectingWatchdogTimerRef.current = setTimeout(() => {
            connectingWatchdogTimerRef.current = null;
            // Only claim "waiting on permission" while the capture chain
            // genuinely hasn't come up — a long connect with the mic
            // ALREADY granted (e.g. ICE parked in 'disconnected' pre-live)
            // must keep the generic connecting copy instead of a
            // misleading permission prompt hint (review finding).
            if (!callStreamRef.current) {
              setWaitingOnMicPermission(true);
            }
          }, CONNECTING_WATCHDOG_MS);
          break;
        case 'CANCEL_CONNECTING_WATCHDOG':
          if (connectingWatchdogTimerRef.current) {
            clearTimeout(connectingWatchdogTimerRef.current);
            connectingWatchdogTimerRef.current = null;
          }
          setWaitingOnMicPermission(false);
          break;
      }
    }
  }, []);

  const dismissCallNotice = useCallback(() => {
    if (noticeTimerRef.current) {
      clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
    setCallNoticeState(null);
  }, []);

  // Retracts a stale WARNING the instant speech proves it wrong — see
  // retractSilenceWarning (lib/call-notice.ts) for the full rationale (every
  // WARNING this hook shows is a "your mic hears nothing" claim, so one
  // speech-level peak falsifies all of them; SUCCESS/ERROR are untouched).
  const dismissSilenceWarning = useCallback(() => {
    setCallNoticeState(retractSilenceWarning);
  }, []);

  // Shows a notice, superseding any current one. SUCCESS auto-clears after
  // SUCCESS_NOTICE_MS ("confirms, then fades"); WARNING/ERROR persist until
  // dismissed, superseded, or a fresh capture chain goes live.
  const showCallNotice = useCallback((notice: CallNotice) => {
    if (noticeTimerRef.current) {
      clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
    setCallNoticeState(notice);
    if (notice.kind === CallNoticeKind.SUCCESS) {
      noticeTimerRef.current = setTimeout(() => {
        noticeTimerRef.current = null;
        setCallNoticeState(null);
      }, SUCCESS_NOTICE_MS);
    }
  }, []);

  /**
   * Shows a "Sara can't hear you" WARNING, offering a one-tap switch to the
   * best-ranked OTHER mic when one exists. This is the whole response to a
   * silent mic now (FB28) — detection stayed, the auto-switch did not: the
   * automatic rebuild-on-next-device could move capture away from the
   * device the user actually hears Sara through (car Bluetooth → phone mic,
   * observed live), creating cross-device echo no AEC can cancel, and then
   * the old signal-based memory persisted that wrong mic for every later
   * call. The user decides; system routing is never overridden silently.
   */
  const showMicSilenceAdvice = useCallback(
    async (opts: {
      title: string;
      subtitle: string;
      /** Shown instead of `subtitle` when there is no other mic to offer —
       *  the only self-help left is outside the browser. */
      noAlternativesSubtitle: string;
      excludeDeviceId: string | undefined;
    }) => {
      let alternatives: MediaDeviceInfo[] = [];
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        alternatives = rankMicCandidates(
          devices,
          new Set(opts.excludeDeviceId ? [opts.excludeDeviceId] : [])
        );
      } catch {
        // Enumeration failed — fall through to the actionless notice.
      }
      if (!isCallingRef.current) return;
      showCallNotice({
        kind: CallNoticeKind.WARNING,
        topic: CallNoticeTopic.MIC_SILENCE,
        title: opts.title,
        subtitle:
          alternatives.length > 0 ? opts.subtitle : opts.noAlternativesSubtitle,
        ...(alternatives.length > 0
          ? {
              action: {
                label: 'Try another mic',
                onClick: () =>
                  void switchMicRef.current?.(alternatives[0].deviceId),
              },
            }
          : {}),
      });
    },
    [showCallNotice]
  );

  // Tears down the CURRENT capture chain (mic stream, analyser, worklet,
  // context) without touching call state — used between mic-switch rebuilds
  // and the startup failure path. stopCall does its own version of this
  // plus the state flips.
  //
  // `keepTrackAlive` (mic-switch gap fix): for the rtc transport, the
  // OUTGOING audio the user actually hears Sara respond to is the raw
  // MediaStreamTrack feeding the RTCRtpSender directly — NOT anything
  // downstream of the AudioContext graph (the analyser/worklet are local-
  // only taps). So the analyser/worklet/context can all be torn down
  // eagerly as always; only the TRACK'S OWN `.stop()` needs to wait until
  // AFTER sender.replaceTrack() has swapped it out, or the outgoing stream
  // goes dead for the whole rebuild (mic permission + worklet setup), not
  // just the swap itself. When true, the live (unstopped) MediaStream is
  // returned so the caller can retire it once the swap is safe.
  const teardownCapture = useCallback(
    (opts?: { keepTrackAlive?: boolean }): MediaStream | null => {
      const stream = callStreamRef.current;
      if (!opts?.keepTrackAlive) {
        stream?.getTracks().forEach((t) => t.stop());
      }
      callStreamRef.current = null;
      callAnalyserRef.current?.disconnect();
      callAnalyserRef.current = null;
      registerUserAnalyser(null);
      callProcessorRef.current?.disconnect();
      callProcessorRef.current = null;
      if (callAudioContextRef.current) {
        try {
          callAudioContextRef.current.close();
        } catch {
          // already closed
        }
        callAudioContextRef.current = null;
      }
      callBufferRef.current = [];
      return opts?.keepTrackAlive ? (stream ?? null) : null;
    },
    [registerUserAnalyser]
  );

  // [rtc-spike] Tears down the CURRENT rtc call's resources — the peer
  // connection, the mic stream (shared with the normal path's
  // callStreamRef), and the hidden remote-audio sink. Mirrors
  // teardownCapture's role for the normal path; used on both a
  // superseded-mid-build abort and a real stopCall. Deliberately does NOT
  // POST the hangup itself (see stopRtcCall) — every call site that has a
  // pc/callId in hand is responsible for calling stopRtcCall BEFORE this,
  // since this function's job is purely local resource release.
  const teardownRtc = useCallback(() => {
    if (rtcPcRef.current) {
      // [rtc-reliability] Detach the listener BEFORE closing — a 'closed'
      // event queued after this point must never reach
      // handleRtcConnectionStateChange (the call is either fully over, or
      // this pc was just superseded by a successful re-call — either way,
      // its own future events are noise the recovery machine must not act
      // on; see performRtcRecall's identical nulling on a mid-call swap).
      rtcPcRef.current.onconnectionstatechange = null;
      rtcPcRef.current.close();
    }
    rtcPcRef.current = null;
    rtcCallIdRef.current = null;
    // Agent-level meter tap (Change 4) — disconnect + hand the engine back
    // to null, same as the mic analyser's teardown in teardownCapture.
    rtcRemoteSourceRef.current?.disconnect();
    rtcRemoteSourceRef.current = null;
    rtcRemoteAnalyserRef.current?.disconnect();
    rtcRemoteAnalyserRef.current = null;
    registerAgentAnalyser(null);
    callStreamRef.current?.getTracks().forEach((t) => t.stop());
    callStreamRef.current = null;
    const sink = rtcSinkElementRef.current;
    if (sink) {
      sink.pause();
      sink.srcObject = null;
      sink.remove();
      rtcSinkElementRef.current = null;
    }
    // [rtc-reliability] This call's recovery machinery is over regardless of
    // which path got here (clean hangup or a mid-build abort) — a stale
    // grace timer must never fire into the NEXT call.
    if (rtcGraceTimerRef.current) {
      clearTimeout(rtcGraceTimerRef.current);
      rtcGraceTimerRef.current = null;
    }
    rtcRecoveryRef.current = initialRtcRecoveryState();
    // [rtc-reliability] Tab-close listener + 60-minute cap timers — all
    // rtc-only, registered together once a call is established, so they're
    // retired together here too.
    if (pageHideHandlerRef.current) {
      window.removeEventListener('pagehide', pageHideHandlerRef.current);
      pageHideHandlerRef.current = null;
    }
    if (callLimitWarnTimerRef.current) {
      clearTimeout(callLimitWarnTimerRef.current);
      callLimitWarnTimerRef.current = null;
    }
    if (callLimitEndTimerRef.current) {
      clearTimeout(callLimitEndTimerRef.current);
      callLimitEndTimerRef.current = null;
    }
    transportRef.current = 'ws';
  }, [registerAgentAnalyser]);

  // [rtc-reliability] Wires a remote (agent) MediaStream into the hidden
  // sink + the agent-level meter tap — extracted out of startCall's rtc
  // branch (where this used to be an inline onRemoteStream closure) so a
  // successful re-call (performRtcRecall) can reuse the EXACT same wiring
  // instead of duplicating it. Tears down any PRIOR remote-level tap first —
  // teardownRtc only runs on a full stop, so a re-call replacing the pc
  // mid-call must retire the OLD pc's tap itself before building the new
  // one, or the old MediaStreamAudioSourceNode leaks.
  const attachRemoteStream = useCallback(
    (remoteStream: MediaStream) => {
      if (rtcSinkElementRef.current) {
        rtcSinkElementRef.current.srcObject = remoteStream;
      }
      rtcRemoteSourceRef.current?.disconnect();
      rtcRemoteAnalyserRef.current?.disconnect();
      const ctx = callAudioContextRef.current;
      if (ctx) {
        // Known WebAudio quirk (see the original wiring this replaces):
        // MediaStreamAudioSourceNode only delivers data once its stream is
        // ALSO attached to a playing media element, which it is
        // (rtcSinkElementRef.current, set just above).
        const source = ctx.createMediaStreamSource(remoteStream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        rtcRemoteSourceRef.current = source;
        rtcRemoteAnalyserRef.current = analyser;
        registerAgentAnalyser(analyser);
      }
    },
    [registerAgentAnalyser]
  );

  // [rtc-reliability] (Re-)arms the 60-minute session cap timers — OpenAI
  // hard-kills realtime sessions at 60m with no warning of its own (see
  // CALL_LIMIT_WARN_MS/CALL_LIMIT_END_MS). Clears any existing handles
  // first: called at ANSWER_RECEIVED time (both rtc paths) AND from
  // performRtcRecall's success branch, because a successful re-call is a
  // BRAND-NEW OpenAI call — minute zero on OpenAI's clock — so the
  // warn/end deadlines must restart from the swap, not keep counting from
  // the original call's start. Safe to arm even if the attempt later fails:
  // teardownRtc() unconditionally clears both handles.
  const armCallLimitTimers = useCallback(() => {
    if (callLimitWarnTimerRef.current) {
      clearTimeout(callLimitWarnTimerRef.current);
    }
    if (callLimitEndTimerRef.current) {
      clearTimeout(callLimitEndTimerRef.current);
    }
    callLimitWarnTimerRef.current = setTimeout(() => {
      callLimitWarnTimerRef.current = null;
      if (!isCallingRef.current) return;
      showCallNotice({
        kind: CallNoticeKind.WARNING,
        topic: CallNoticeTopic.CONNECTION,
        title: 'This call is reaching its length limit.',
        subtitle: 'Sara may need to call again soon.',
      });
    }, CALL_LIMIT_WARN_MS);
    callLimitEndTimerRef.current = setTimeout(() => {
      callLimitEndTimerRef.current = null;
      if (!isCallingRef.current) return;
      endCallWithRetryChipRef.current(
        'That call reached its time limit.',
        "Start a new call whenever you're ready."
      );
    }, CALL_LIMIT_END_MS);
  }, [showCallNotice]);

  /**
   * Builds the full capture chain for ONE microphone (remembered device,
   * Chrome's default, or an explicit deviceId on a user switch) and arms
   * the frame pump + silence watchdog. Returns the live audio track, or
   * null if the call was stopped/superseded mid-build. `onSilent` fires at
   * most once per built chain — near-zero peak for ~3s of capture (see
   * MIC_WATCHDOG_POLICY).
   */
  const buildCapture = useCallback(
    async (opts: BuildCaptureOptions): Promise<MediaStreamTrack | null> => {
      // NO sampleRate option: the context must run at the DEVICE'S native
      // rate. Forcing 24kHz against a 48kHz mic track made Chrome/macOS
      // deliver a healthy-looking graph of pure zeros (peak=0.000 on every
      // chunk) — the 24kHz backend contract is met by resampling explicitly
      // in encodePcm16Chunk instead.
      const audioCtx = opts.gestureCtx ?? new AudioContext();
      callAudioContextRef.current = audioCtx;
      if (audioCtx.state === 'suspended') {
        // Inside the gesture (initial) this is permitted directly; on a
        // switch rebuild the call-start click's sticky activation covers
        // it. Fire and forget — the running-state gate below is the
        // arbiter.
        void audioCtx.resume().catch(() => undefined);
      }
      const ctxStateAtCreate = audioCtx.state;

      // Dynamically create the AudioWorklet processor for frame capture.
      // Each context has its own worklet global scope, so the module is
      // (re)added per build. Kicked off BEFORE getUserMedia so the two
      // independent async setups overlap; awaited (with the abort check)
      // right before the node is constructed. The .finally guarantees the
      // blob URL is revoked on every path — resolution, rejection, or an
      // early abort that never reaches the await — and the no-op .catch
      // prevents an unhandled rejection when getUserMedia throws first (the
      // real rejection still surfaces at the await into the caller's
      // try/catch).
      const workletCode = `
        class RecorderProcessor extends AudioWorkletProcessor {
          process(inputs) {
            const input = inputs[0];
            if (input && input[0]) {
              this.port.postMessage(input[0]);
            }
            return true;
          }
        }
        registerProcessor('recorder-processor', RecorderProcessor);
      `;
      const blob = new Blob([workletCode], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);
      const workletReady = audioCtx.audioWorklet
        .addModule(workletUrl)
        .finally(() => URL.revokeObjectURL(workletUrl));
      workletReady.catch(() => undefined);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...(opts.deviceId
            ? {
                deviceId: opts.exactDevice
                  ? { exact: opts.deviceId }
                  : opts.deviceId,
              }
            : {}),
          ...MIC_AUDIO_CONSTRAINTS,
        },
      });
      if (opts.aborted()) {
        stream.getTracks().forEach((t) => t.stop());
        teardownCapture();
        return null;
      }
      callStreamRef.current = stream;
      // A mic-switch rebuild must inherit the user's current mute — the
      // fresh track defaults to enabled and would silently unmute them.
      stream.getAudioTracks().forEach((t) => {
        t.enabled = !isMutedRef.current;
      });

      // Structured capture diagnostics. Voice capture has repeatedly failed
      // SILENTLY (suspended context, dropped frames, silent devices) — there
      // is no Sentry here, so these lines are the only forensic trail; Next
      // dev also forwards them into the server log. Keep them.
      const track = stream.getAudioTracks()[0];
      console.warn(
        `[voice] capture start: ctx=${ctxStateAtCreate}→${audioCtx.state}` +
          ` ctxRate=${audioCtx.sampleRate}` +
          ` mic="${track?.label}" muted=${track?.muted}` +
          ` enabled=${track?.enabled} readyState=${track?.readyState}` +
          ` settings=${JSON.stringify(track?.getSettings?.() ?? {})}`
      );

      // Create the analyser and register it with the level engine — no
      // local analysis loop here anymore (see file header). fftSize is the
      // only setting that matters for time-domain RMS reads;
      // smoothingTimeConstant only affects getFloat/ByteFrequencyData
      // (frequency-domain), so it's omitted rather than set-and-ignored.
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      callAnalyserRef.current = analyser;
      registerUserAnalyser(analyser);

      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);

      // Module load started before getUserMedia (see above) — by now it has
      // usually finished; a rejection throws into the caller's try/catch.
      await workletReady;
      if (opts.aborted()) {
        teardownCapture();
        return null;
      }
      const workletNode = new AudioWorkletNode(audioCtx, 'recorder-processor');
      callProcessorRef.current = workletNode;

      source.connect(workletNode);

      // Gate the chain on the context actually RUNNING. A context stuck in
      // 'suspended' means zero captured frames — the exact silent-call failure
      // this hook regressed into — so surface it as a startup error instead.
      // resume() can also hang indefinitely on some platforms, hence the race.
      if (audioCtx.state !== 'running') {
        await Promise.race([
          audioCtx.resume(),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error('AudioContext resume timed out')),
              2000
            )
          ),
        ]);
      }
      if (opts.aborted()) {
        teardownCapture();
        return null;
      }
      if ((audioCtx.state as AudioContextState) !== 'running') {
        throw new Error(
          `AudioContext is '${audioCtx.state}' — cannot capture microphone`
        );
      }

      setActiveMicId(track?.getSettings?.().deviceId ?? null);
      // A fresh chain going live supersedes any prior mic advisory — the
      // caller may immediately follow with a success notice.
      dismissCallNotice();

      // Capture-pipeline diagnostics: first worklet frame proves the audio
      // graph is rendering; the periodic line proves chunks are leaving on an
      // OPEN socket and carries the peak amplitude so a live-but-silent mic
      // is distinguishable from a dead pipeline.
      let workletFrames = 0;
      let chunksSent = 0;
      let chunksDropped = 0;
      let peakSinceLog = 0;
      // Silence watchdog — one near-zero stage firing at most once per
      // built chain. Threshold, timing, and the Bluetooth-warmup rationale
      // for having exactly one (slow) stage live on MIC_WATCHDOG_POLICY
      // (module top). Peak tracked from capture start (never reset, unlike
      // peakSinceLog).
      let watchdogPeak = 0;
      let watchdogFired = false;
      let speechSeen = false;

      // Capture runs at the context's native rate; encodePcm16Chunk resamples
      // each ~100ms accumulation down to the backend's 24kHz on the way out.
      const inputRate = audioCtx.sampleRate;
      callInputRateRef.current = inputRate;
      const chunkInputSamples = Math.round(inputRate * CHUNK_SECONDS);

      // Frame accumulation and WebSocket send
      workletNode.port.onmessage = (e) => {
        if (!isCallingRef.current) return;
        const frame = e.data as Float32Array;
        if (workletFrames === 0) {
          console.info('[voice] first worklet frame received');
        }
        workletFrames += 1;

        callBufferRef.current.push(frame);
        let totalLength = 0;
        for (const chunk of callBufferRef.current) {
          totalLength += chunk.length;
        }

        if (totalLength >= chunkInputSamples) {
          const framePeak = peakAmplitude(callBufferRef.current);
          if (framePeak > peakSinceLog) peakSinceLog = framePeak;
          if (framePeak > watchdogPeak) watchdogPeak = framePeak;

          if (
            !speechSeen &&
            framePeak > MIC_WATCHDOG_POLICY.SPEECH_PEAK_THRESHOLD
          ) {
            speechSeen = true;
            // The user IS being heard — retract any stale "Sara can't hear
            // you" WARNING right here, at the exact edge the evidence
            // arrives (see dismissSilenceWarning's declaration comment for
            // why this is sound for every WARNING in this hook).
            dismissSilenceWarning();
          }

          const data = encodePcm16Chunk(callBufferRef.current, inputRate);
          callBufferRef.current = [];

          const chunkIndex = chunksSent + chunksDropped + 1;
          const trackLive = track?.enabled !== false; // user-mute isn't a fault

          if (
            !watchdogFired &&
            chunkIndex >= MIC_WATCHDOG_POLICY.SILENCE_WATCHDOG_CHUNKS &&
            watchdogPeak < MIC_WATCHDOG_POLICY.SILENCE_PEAK_THRESHOLD &&
            trackLive
          ) {
            watchdogFired = true;
            console.warn(
              `[voice] silence watchdog: mic "${track?.label}" delivered no` +
                ` signal in the first ~3s of capture`
            );
            opts.onSilent(track);
          }

          // "Sara can't hear you" nudge — the second rung of the ladder:
          // the watchdog above catches a DEAD mic (zeros / near-zeros);
          // this catches an ALIVE mic (noise floor present, so the watchdog
          // never fires) that never registers speech-level peaks in ~15s —
          // typically the user is talking into a different physical mic
          // than the one captured. Once per call (nudgeShownRef), never
          // while muted (trackLive), never on top of a watchdog advisory.
          if (
            !nudgeShownRef.current &&
            !speechSeen &&
            !watchdogFired &&
            trackLive &&
            chunkIndex >= MIC_WATCHDOG_POLICY.NUDGE_CHUNKS
          ) {
            nudgeShownRef.current = true;
            console.warn(
              '[voice] no speech detected in first ~15s of call (mic alive)'
            );
            void showMicSilenceAdvice({
              title: "If you're speaking, Sara can't hear you.",
              subtitle: 'Your mic has been silent for a few seconds.',
              noAlternativesSubtitle:
                'Check your microphone volume in system settings.',
              excludeDeviceId: track?.getSettings?.().deviceId,
            });
          }

          // [rtc-spike] transportRef (not rtcPcRef — see its declaration
          // comment) gates the send: an rtc call routes audio over the
          // RTCPeerConnection instead, and the backend also drops any
          // stray {type:'audio'} WS frames while an rtc call is attached,
          // but sending them anyway would waste bandwidth for nothing.
          // Falling into the existing chunksDropped branch (rather than a
          // separate no-op) is deliberate: chunkIndex above is derived from
          // chunksSent + chunksDropped, so this keeps it advancing and the
          // silence watchdog/nudge timing above working identically for an
          // rtc call — only the WS send itself (and its success counter)
          // is skipped.
          if (
            transportRef.current === 'ws' &&
            wsRef.current?.readyState === WebSocket.OPEN
          ) {
            wsRef.current.send(
              JSON.stringify({ type: 'audio', data, commit: false })
            );
            chunksSent += 1;
          } else {
            chunksDropped += 1;
          }

          // Every ~5s of audio (50 x 100ms chunks), or on the very first chunk.
          if (
            chunksSent + chunksDropped === 1 ||
            (chunksSent + chunksDropped) % 50 === 0
          ) {
            console.info(
              `[voice] chunks sent=${chunksSent} dropped=${chunksDropped}` +
                ` peak=${peakSinceLog.toFixed(3)}` +
                ` ws=${wsRef.current ? wsRef.current.readyState : 'null'}`
            );
            peakSinceLog = 0;
          }
        }
      };

      return track ?? null;
    },
    [
      wsRef,
      registerUserAnalyser,
      teardownCapture,
      showMicSilenceAdvice,
      dismissCallNotice,
      dismissSilenceWarning,
    ]
  );

  // Enumerates devices and stashes the audioinput list for the mic-switcher
  // UI. Safe to call any time (labels are empty pre-permission, but the list
  // itself never throws in supported browsers) — guarded anyway since
  // enumerateDevices can reject in some embedded/webview contexts, and this
  // list is cosmetic, not load-bearing for capture itself.
  const refreshAudioInputs = useCallback(async () => {
    try {
      setAudioDevices(await navigator.mediaDevices.enumerateDevices());
    } catch {
      // Cosmetic list only — ignore.
    }
  }, []);

  // [FB34] Shared tail once a call has genuinely gone live (mic flowing,
  // rtc: track attached) — flips the INTERNAL isCalling signal (today's
  // point, unchanged by FB34; see file header) and starts device
  // enumeration. Common to all three start paths (ws, rtc-sequential,
  // rtc-parallel).
  const finalizeCallLive = useCallback(() => {
    isCallingRef.current = true;
    setIsCalling(true);
    void refreshAudioInputs();
    if (typeof navigator.mediaDevices.addEventListener === 'function') {
      const handleDeviceChange = () => void refreshAudioInputs();
      deviceChangeHandlerRef.current = handleDeviceChange;
      navigator.mediaDevices.addEventListener(
        'devicechange',
        handleDeviceChange
      );
    }
  }, [refreshAudioInputs]);

  // [rtc-reliability] Tab-close hangup — registered once an rtc call has a
  // real callId to target. sendBeacon can't carry the auth header
  // (X-Device-Token), so a keepalive fetch is the deliberate choice here
  // instead.
  const registerRtcPageHideHandler = useCallback(
    (callId: string) => {
      console.info(`[rtc-spike] call started call_id=${callId}`);
      const handlePageHide = () => {
        if (transportRef.current !== 'rtc') return;
        void fetch(getAssistantApiUrl('/api/rtc/hangup'), {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Token': getDeviceToken(),
          },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            // Read at pagehide time, not captured: a mid-call recall
            // (performRtcRecall) swaps in a new call id, and the hangup
            // must target whichever call is live when the tab closes.
            ...(rtcCallIdRef.current ? { call_id: rtcCallIdRef.current } : {}),
          }),
        });
      };
      window.addEventListener('pagehide', handlePageHide);
      pageHideHandlerRef.current = handlePageHide;
      void callId;
    },
    [sessionIdRef]
  );

  /**
   * [FB34] The async body shared by a fresh startCall() and a retryCall() —
   * the caller has ALREADY verified the ws-open precondition and dispatched
   * the phase-entry event (START_TAPPED or RETRY_TAPPED, landing on
   * `connecting`) before invoking this. Handles both transports; for rtc,
   * gates on canOverlapCaptureWithCall to decide sequential vs the
   * latency-overlap parallel path (see rtc-call.ts's startRtcCallOffer).
   */
  const runStartAttempt = useCallback(
    async (useRtc: boolean) => {
      const myGeneration = callGenerationRef.current + 1;
      callGenerationRef.current = myGeneration;
      const aborted = () => callGenerationRef.current !== myGeneration;
      // The generation trips `aborted()` for TWO distinct reasons, and the
      // failure branches below must tell them apart:
      //   1. This attempt's OWN arm failed (bumpGenerationOnce below) — the
      //      bump exists purely to make the sibling arm settle cleanly; the
      //      attempt still owns its resources and MUST show its failure UI.
      //   2. EXTERNAL supersession (stopCall, or a newer attempt) — the
      //      shared refs (rtcPcRef/callStreamRef/…) may now belong to a
      //      DIFFERENT, live call, so a late-settling promise from this
      //      attempt must NOT run teardownRtc()/teardownCapture() or
      //      dispatch failures: doing so tore down the newer call's mic/pc
      //      while its UI kept saying `live` (review blocker, 2026-07-17).
      let failureBumped = false;
      const bumpGenerationOnce = () => {
        if (failureBumped) return;
        // Ownership guard: if something ELSE already moved the counter,
        // this attempt is dead — bumping again would spuriously abort an
        // unrelated newer attempt that now owns the counter.
        if (callGenerationRef.current !== myGeneration) return;
        failureBumped = true;
        callGenerationRef.current += 1;
      };
      // External supersession only: our own failure bump moves the counter
      // to exactly myGeneration + 1 with failureBumped set — anything else
      // is someone else's doing.
      const superseded = () =>
        callGenerationRef.current !== myGeneration &&
        !(failureBumped && callGenerationRef.current === myGeneration + 1);

      // [FB34] Timing-mark origin for this attempt — see
      // voiceTimingStartRef's declaration comment.
      voiceTimingStartRef.current = performance.now();
      logVoiceTiming('tap');

      // Per-attempt nudge guard (see the pump) — a fresh attempt gets a
      // fresh nudge, including a retry.
      nudgeShownRef.current = false;
      connectionLostRef.current = false;

      // Silent-mic response (FB28): advisory only — see showMicSilenceAdvice's
      // declaration comment for why the auto-switch this used to trigger is
      // gone. The one automatic thing that still happens: a REMEMBERED mic
      // that proves dead is forgotten, so the next call starts on the
      // browser/OS default instead of retrying the same dead device.
      const handleSilentCapture = (
        silentTrack: MediaStreamTrack | undefined
      ): void => {
        const currentId = silentTrack?.getSettings?.().deviceId;
        if (currentId && currentId === readPreferredMicId()) {
          clearPreferredMicId();
        }
        void showMicSilenceAdvice({
          title: "Sara can't hear you.",
          subtitle: 'Your microphone is picking up no sound.',
          noAlternativesSubtitle:
            'Your microphone is picking up no sound. If your laptop lid is ' +
            'closed, open it or check your system sound settings.',
          excludeDeviceId: currentId,
        });
      };

      try {
        setIsMuted(false);
        isMutedRef.current = false;
        callStartedAtRef.current = Date.now();

        // Set BEFORE buildCapture (see transportRef's declaration comment
        // for why the timing matters) — reset back to 'ws' in teardownRtc.
        transportRef.current = useRtc ? 'rtc' : 'ws';

        if (useRtc) {
          // Hidden sink for the remote (agent) track — created and
          // .play()'d BEFORE either arm's first await below so it inherits
          // the click's transient user-activation, same reasoning as
          // gestureCtx/primePlayback: a media element built/played after
          // the gesture has lapsed can come up autoplay-blocked. It also
          // doubles as this transport's AEC reference media element (see
          // src/lib/audio-sink.ts's header for why that hop matters), which
          // is why primePlayback (the normal path's own <audio> sink) is
          // skipped below for this transport. srcObject is assigned once
          // the remote track arrives — see attachRemoteStream.
          rtcSinkElementRef.current = createHiddenAudioSink('[rtc-spike]');
        }

        // Create the initial AudioContext SYNCHRONOUSLY — before either
        // arm's first await, while the click's transient user activation is
        // still live. The browser decides running-vs-suspended at
        // construction time: created here it starts running; created after
        // a permission-prompt delay (Chrome's one-time "Allow this time"
        // grant re-prompts on EVERY call) it starts suspended, the capture
        // worklet never processes a frame, and the call sits silent in both
        // directions with no error. Shared by both transports — the rtc
        // branch needs the SAME capture chain as the normal one; only what
        // happens to the outgoing audio differs.
        const gestureCtx = new AudioContext();

        if (!useRtc) {
          // Same gesture-time rule for the PLAYBACK side (see the option's
          // doc comment): unlock Sara's <audio> sink while the click's
          // activation is still live. Not needed for rtc — its own hidden
          // sink (above) already serves that role for this transport.
          primePlayback();

          // ── ws transport — unchanged sequential capture ──
          const track = await buildCapture({
            deviceId: readPreferredMicId() ?? undefined,
            exactDevice: false,
            gestureCtx,
            aborted,
            onSilent: handleSilentCapture,
          });
          if (!track && aborted()) return; // stopped mid-build; already cleaned up
          if (!track) return; // buildCapture's own contract: null only means aborted.

          micGrantedThisSessionRef.current = true;
          logVoiceTiming('capture-ready');
          dispatchCallPhase({ type: 'CAPTURE_READY' }); // ws: connecting → live
          finalizeCallLive();
          return;
        }

        // ── rtc transport ──
        const canOverlap = await canOverlapCaptureWithCall(
          micGrantedThisSessionRef.current
        );
        if (aborted()) {
          // Superseded while awaiting the (async) permission check itself —
          // nothing rtc-specific was created yet beyond the sink.
          teardownCapture();
          teardownRtc();
          return;
        }

        if (!canOverlap) {
          // ── sequential rtc (today's path — latency unchanged) ──
          const track = await buildCapture({
            deviceId: readPreferredMicId() ?? undefined,
            exactDevice: false,
            gestureCtx,
            aborted,
            onSilent: handleSilentCapture,
          });
          if (!track && aborted()) return;
          if (!track) {
            // Vanishingly rare (getUserMedia granted with zero audio
            // tracks) but not an abort — throw so the outer catch fails
            // loudly via the failed-phase UI instead of silently "starting"
            // an rtc call with nothing to send.
            throw new Error('[rtc-spike] buildCapture returned no audio track');
          }
          micGrantedThisSessionRef.current = true;
          logVoiceTiming('capture-ready');
          dispatchCallPhase({ type: 'CAPTURE_READY' });

          const { pc, callId } = await startRtcCall({
            micTrack: track,
            // buildCapture stores the stream here; non-null because a
            // non-null track above can only come from a stream buildCapture
            // successfully assigned into it.
            micStream: callStreamRef.current!,
            sessionId: sessionIdRef.current,
            onRemoteStream: attachRemoteStream,
            // [rtc-reliability] Feeds src/lib/rtc-recovery.ts's state
            // machine — see handleRtcConnectionStateChange's declaration
            // below.
            onConnectionStateChange: (state) =>
              handleRtcConnectionStateChangeRef.current(state),
            // [SDP-over-WS] see rtcWsSend's declaration comment above.
            wsSend: rtcWsSend(wsRef.current),
          });
          if (aborted()) {
            // stopCall (or a newer attempt) raced this build — hang up the
            // call it just created (this `pc`, not yet in rtcPcRef, is the
            // one thing an earlier stopCall couldn't have closed).
            void stopRtcCall(pc, sessionIdRef.current, callId);
            teardownRtc();
            teardownCapture();
            return;
          }
          // [FB34] Assigned at ANSWER time (before track attachment, which
          // for the sequential path already happened via addTrack inside
          // startRtcCall) — see rtcPcRef's declaration comment.
          rtcPcRef.current = pc;
          rtcCallIdRef.current = callId;
          logVoiceTiming('answer');
          dispatchCallPhase({ type: 'ANSWER_RECEIVED' });
          armCallLimitTimers();
          logVoiceTiming('track-attached'); // addTrack already happened above
          dispatchCallPhase({ type: 'TRACK_ATTACHED' });
          registerRtcPageHideHandler(callId);
          finalizeCallLive();
          return;
        }

        // ── parallel rtc (FB34 latency overlap) ──
        // Either arm failing bumps the call generation ONCE (via
        // `aborted()` above being re-derived from callGenerationRef) so the
        // sibling settles cleanly instead of a later grant/answer building
        // an orphaned live mic chain on a call that's already failed —
        // see bumpGenerationOnce/superseded at the top of this function.
        const callArmPromise = startRtcCallOffer({
          sessionId: sessionIdRef.current,
          onRemoteStream: attachRemoteStream,
          onConnectionStateChange: (state) =>
            handleRtcConnectionStateChangeRef.current(state),
          // [SDP-over-WS] see rtcWsSend's declaration comment above.
          wsSend: rtcWsSend(wsRef.current),
        });
        const captureArmPromise = buildCapture({
          deviceId: readPreferredMicId() ?? undefined,
          exactDevice: false,
          gestureCtx,
          aborted,
          onSilent: handleSilentCapture,
        });

        const watchedCallArm = callArmPromise
          .then((result) => {
            if (aborted()) {
              // Superseded (stopCall ran, or the sibling capture arm
              // already failed and bumped the generation) BEFORE this arm
              // resolved — nothing will discover this call via rtcPcRef
              // (never assigned), so hang it up right here instead of
              // waiting on whatever the sibling is still doing.
              void stopRtcCall(result.pc, sessionIdRef.current, result.callId);
              return result;
            }
            // Publish immediately (NOT gated on the capture arm) so a
            // stopCall that runs WHILE capture is still pending — e.g. the
            // user hasn't answered the mic permission prompt yet — finds
            // this via rtcPcRef and hangs it up right away, rather than
            // only discovering it once both arms join below.
            rtcPcRef.current = result.pc;
            rtcCallIdRef.current = result.callId;
            logVoiceTiming('answer');
            dispatchCallPhase({ type: 'ANSWER_RECEIVED' });
            armCallLimitTimers();
            return result;
          })
          .catch((err) => {
            bumpGenerationOnce();
            throw err;
          });

        const watchedCaptureArm = captureArmPromise
          .then((track) => {
            if (track) {
              micGrantedThisSessionRef.current = true;
              logVoiceTiming('capture-ready');
              dispatchCallPhase({ type: 'CAPTURE_READY' });
            }
            return track;
          })
          .catch((err) => {
            bumpGenerationOnce();
            throw err;
          });

        const [captureSettled, callSettled] = await Promise.allSettled([
          watchedCaptureArm,
          watchedCallArm,
        ]);

        // ── Arm-failure choreography ──
        // Every branch below runs AFTER the allSettled await, which can
        // span the 15s call timeout or an indefinite permission prompt —
        // long enough for stopCall + a whole NEW call to have happened.
        // `superseded()` gates each one: when external supersession
        // happened, the shared refs belong to someone else now, so the
        // stale branch must only clean up its own LOCALS (its pc/track),
        // never the shared teardown paths, and must not dispatch failures.
        if (captureSettled.status === 'rejected') {
          if (superseded()) {
            // Our created call (if any) was either self-hung-up by the
            // call arm's own aborted() check or by stopCall via rtcPcRef;
            // nothing else here is ours anymore.
            return;
          }
          // gUM denied (or another capture-side throw) — this reason ALWAYS
          // wins over a call-arm outcome, matching what the user actually
          // experienced (no mic, regardless of whether the call was ready).
          if (callSettled.status === 'fulfilled') {
            void stopRtcCall(
              callSettled.value.pc,
              sessionIdRef.current,
              callSettled.value.callId
            );
          }
          teardownCapture();
          teardownRtc();
          dispatchCallPhase({
            type: 'START_FAILED',
            reason: classifyStartFailure(captureSettled.reason),
          });
          return;
        }
        if (callSettled.status === 'rejected') {
          if (superseded()) {
            // Stop only OUR track — the shared capture refs may already be
            // the next call's chain.
            captureSettled.value?.stop();
            return;
          }
          // POST failed (capture succeeded, or was itself superseded to
          // null by the capture arm's own abort-check) — stop whatever
          // capture produced; nothing to hang up server-side (the call
          // never existed, or rtc-call.ts's own catch already closed the
          // half-built pc locally).
          teardownCapture();
          teardownRtc();
          dispatchCallPhase({
            type: 'START_FAILED',
            reason: classifyStartFailure(callSettled.reason),
          });
          return;
        }

        // Both arms settled without throwing.
        if (aborted()) {
          // Superseded — hang up OUR call and stop OUR track (locals only:
          // buildCapture's internal check / the call arm's early
          // self-hangup / stopCall's direct rtcPcRef-based hangup already
          // cover most paths, this is the idempotent mop-up), but never
          // the shared teardown helpers — the refs they read may already
          // belong to a newer live call.
          void stopRtcCall(
            callSettled.value.pc,
            sessionIdRef.current,
            callSettled.value.callId
          );
          captureSettled.value?.stop();
          return;
        }

        const track = captureSettled.value;
        const { pc, callId, attachTrack } = callSettled.value;
        if (!track) {
          // Contract-violation guard (buildCapture should only ever return
          // null when aborted, which the check above already handles) —
          // treat defensively as a backend failure rather than silently
          // going live with no mic.
          void stopRtcCall(pc, sessionIdRef.current, callId);
          teardownRtc();
          teardownCapture();
          dispatchCallPhase({ type: 'START_FAILED', reason: 'backend' });
          return;
        }

        try {
          await attachTrack(track);
        } catch (err) {
          if (superseded()) {
            // A stale attachTrack rejection (its pc was closed by stopCall
            // and a NEW call may be live on the shared refs) — hang up our
            // own call/track only.
            void stopRtcCall(pc, sessionIdRef.current, callId);
            track.stop();
            return;
          }
          void stopRtcCall(pc, sessionIdRef.current, callId);
          teardownRtc();
          teardownCapture();
          dispatchCallPhase({
            type: 'START_FAILED',
            reason: classifyStartFailure(err),
          });
          return;
        }

        logVoiceTiming('track-attached');
        dispatchCallPhase({ type: 'TRACK_ATTACHED' });
        registerRtcPageHideHandler(callId);
        finalizeCallLive();
      } catch (err) {
        // A late throw from a superseded attempt (e.g. the sequential
        // path's startRtcCall rejecting after stopCall already ran) must
        // not touch the shared refs — they may belong to a newer live call
        // — and must not dispatch a failure over whatever the user is
        // doing now.
        if (superseded()) return;
        // Tear down anything we managed to create, then surface the
        // failure IN-SURFACE (the call screen is already mounted — see
        // call-phase.ts's header) rather than a pre-mount toast.
        // teardownRtc() is safe to call unconditionally — every field it
        // touches is optional-chained/no-op when nothing rtc-specific was
        // ever created — so one shared cleanup covers both transports'
        // failure paths.
        teardownCapture();
        teardownRtc();
        dispatchCallPhase({
          type: 'START_FAILED',
          reason: classifyStartFailure(err),
        });
      }
    },
    [
      buildCapture,
      teardownCapture,
      teardownRtc,
      showMicSilenceAdvice,
      primePlayback,
      sessionIdRef,
      attachRemoteStream,
      armCallLimitTimers,
      dispatchCallPhase,
      finalizeCallLive,
      registerRtcPageHideHandler,
      logVoiceTiming,
      wsRef,
    ]
  );

  const startCall = useCallback(async () => {
    // [FB34] Reentrancy guard now checks PHASE, not isCalling — isCalling is
    // still false throughout `connecting`, so the old isCalling-only guard
    // let a second tap during connecting start a SECOND concurrent call.
    if (callPhaseRef.current.phase !== 'idle') return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      // Tapping the call button during a reconnect used to be a silent
      // no-op — on a flapping network the user taps repeatedly and nothing
      // visibly happens (FB28). Say why instead. Pre-mount (no call surface
      // exists yet to host an in-surface notice) — unchanged from before
      // FB34; a RETRY hitting this same guard renders in-surface instead
      // (see retryCall below), because by then the surface is already up.
      toast.error(
        'Still reconnecting to Sara — try the call again in a moment.'
      );
      return;
    }
    const useRtc = isRtcFlagEnabled();
    dispatchCallPhase({
      type: 'START_TAPPED',
      transport: useRtc ? 'rtc' : 'ws',
    });
    await runStartAttempt(useRtc);
  }, [wsRef, dispatchCallPhase, runStartAttempt]);
  // Keep the "Call again" chip's escape hatch pointing at the CURRENT
  // startCall (see the ref's declaration comment for why this indirection
  // exists).
  startCallRef.current = startCall;

  // [FB34] The failed-phase "Try again" chip's dedicated entry point —
  // distinct from startCall because it re-enters via RETRY_TAPPED (reusing
  // the failed attempt's own transport) rather than START_TAPPED, and tears
  // down any residue of the previous attempt FIRST. A plain onClick handler
  // in the call surface can call this directly (real user activation, no
  // stale-closure ref indirection needed — unlike the toast-based "Call
  // again" chips above, this one is rendered fresh by React on every
  // render).
  const retryCall = useCallback(() => {
    if (callPhaseRef.current.phase !== 'failed') return;
    const transport: CallTransport = callPhaseRef.current.transport ?? 'rtc';
    dispatchCallPhase({ type: 'RETRY_TAPPED' });
    // Defensive: every START_FAILED dispatch site above already tore its
    // own attempt down before dispatching, but retry guarantees a clean
    // slate regardless — idempotent, a no-op if already clear.
    teardownCapture();
    teardownRtc();
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      // Unlike startCall's own pre-mount guard above, the call surface is
      // ALREADY showing `connecting` at this point (RETRY_TAPPED just
      // landed) — render this in-surface instead of a toast over it.
      dispatchCallPhase({ type: 'START_FAILED', reason: 'ws_closed' });
      return;
    }
    void runStartAttempt(transport === 'rtc');
  }, [wsRef, dispatchCallPhase, teardownCapture, teardownRtc, runStartAttempt]);

  const stopCall = useCallback(() => {
    // Invalidate any startCall/switchMic still awaiting getUserMedia/
    // addModule so it aborts instead of resuming into an orphaned mic/
    // AudioContext.
    callGenerationRef.current += 1;
    isCallingRef.current = false;
    setIsCalling(false);
    callStartedAtRef.current = null;

    // [rtc-spike] Captured BEFORE teardownRtc resets it to 'ws' — used
    // below to skip the WS tail-flush (see that guard's comment). Everything
    // else in this function (state resets, notices, connection bookkeeping,
    // and the "cleanup audio nodes" block's callStreamRef stop) is
    // transport-agnostic and runs unchanged for both paths — the rtc mic
    // stream lives in the SAME callStreamRef the normal path uses.
    const wasRtc = transportRef.current === 'rtc';
    // Captured BEFORE teardownRtc nulls the refs — needed to close the peer
    // connection + notify the backend (call-scoped: see rtcCallIdRef).
    const rtcPc = rtcPcRef.current;
    const rtcCallId = rtcCallIdRef.current;
    if (rtcPc) {
      // [SDP-over-WS] see rtcWsSend's declaration comment above.
      void stopRtcCall(
        rtcPc,
        sessionIdRef.current,
        rtcCallId ?? undefined,
        rtcWsSend(wsRef.current)
      );
    }
    // [FB34] Keyed on TRANSPORT, not `rtcPc` truthiness — during
    // `connecting` the pc ref may not be populated yet (a fast hangup
    // racing the SDP exchange), and today's guard used to skip this
    // entirely in that window, leaking the hidden sink + the per-call
    // rtc-only timers/recovery state. teardownRtc() is fully idempotent
    // (every field it touches is optional-chained), so calling it whenever
    // this attempt/call WAS rtc — pc or no pc — is always safe.
    if (wasRtc) {
      teardownRtc();
    }
    // NOTE: no caption-tail commit here anymore. TRANSCRIPT_DELTA now routes
    // straight into `messages` via appendAssistantDelta — the SAME pipeline
    // text mode uses (see useAssistantConnection.ts's TRANSCRIPT_DELTA
    // handler) — so Sara's in-flight words are already a durable,
    // upsert-by-item-id `messages` entry the moment they stream. An early
    // hang-up loses nothing that needs a special commit path; the
    // authoritative final transcript reconciles the same message in place
    // when it lands, same as it always has for text turns.
    setIsMuted(false);
    isMutedRef.current = false;
    setActiveMicId(null);
    connectionLostRef.current = false;
    dismissCallNotice();
    // Turn 15: call end is one of the "other reasons" retractions that
    // stays instant (see handleModelResponded's header comment) — clear the
    // dim/resting override and cancel any in-flight release-animation
    // snapshot/timer rather than letting a stale one outlive the call.
    setModelBusy(null);
    if (releaseTimerRef.current) {
      clearTimeout(releaseTimerRef.current);
      releaseTimerRef.current = null;
    }
    setReleasingNotice(null);
    resetToIdle();
    // Silence Sara immediately — the queued tail of her answer would
    // otherwise keep playing over a call that no longer exists (see the
    // clearPlayback option doc).
    clearPlayback();

    if (
      deviceChangeHandlerRef.current &&
      typeof navigator.mediaDevices.removeEventListener === 'function'
    ) {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        deviceChangeHandlerRef.current
      );
      deviceChangeHandlerRef.current = null;
    }

    // Flush any remaining buffered audio, but DO NOT commit it.
    //
    // The realtime session runs with server-side VAD (turn_detection:
    // server_vad) — OpenAI owns utterance boundaries and auto-commits the
    // input buffer when it detects end-of-speech. A client-side commit races
    // that authority: if VAD has already drained the buffer, our commit lands
    // on 0ms of audio and OpenAI raises input_audio_buffer_commit_empty. That
    // error was tearing the whole call down (see QA voice "stop → re-invoke →
    // speak" repro). So we append the tail as a normal frame (commit: false,
    // lossless) and let server VAD finalize the turn. Never send commit: true
    // while server_vad is the configured turn detector.
    //
    // [rtc-spike] Capture is now SHARED with the normal path (buildCapture),
    // so the AudioWorklet pump DOES still fill callBufferRef during an rtc
    // call — it's just never sent over WS (see the pump's transportRef
    // gate). The buffer is drained either way (a leftover partial chunk
    // must not leak into the next call's first send), but only
    // encoded+sent for the ws transport (`!wasRtc` guard): an rtc call's
    // leftover tail has nowhere useful to go — that mic audio already rode
    // the RTCPeerConnection live, so flushing it over WS here would be the
    // exact double-audio the backend's drop-while-attached guard exists to
    // catch. Skip it instead of relying on the backend as the only line of
    // defense.
    if (callBufferRef.current.length > 0) {
      const pending = callBufferRef.current;
      callBufferRef.current = [];

      if (!wasRtc && wsRef.current?.readyState === WebSocket.OPEN) {
        const data = encodePcm16Chunk(pending, callInputRateRef.current);
        wsRef.current.send(
          JSON.stringify({ type: 'audio', data, commit: false })
        );
      }
    }

    // Cleanup audio nodes
    callAnalyserRef.current?.disconnect();
    callAnalyserRef.current = null;
    registerUserAnalyser(null);

    callProcessorRef.current?.disconnect();
    callProcessorRef.current = null;

    callStreamRef.current?.getTracks().forEach((t) => t.stop());
    callStreamRef.current = null;

    if (callAudioContextRef.current) {
      try {
        callAudioContextRef.current.close();
      } catch {
        // Already closed
      }
      callAudioContextRef.current = null;
    }

    // [FB34] {connecting, live, failed} → ending → idle. Both dispatches
    // happen synchronously in this same tick (no async work between them —
    // every cleanup step above is synchronous), so React batches them into
    // one render; `ending` itself is never independently visible from a
    // real stopCall() call (only from a direct reducer unit test).
    dispatchCallPhase({ type: 'END_TAPPED' });
    dispatchCallPhase({ type: 'TEARDOWN_DONE' });
  }, [
    wsRef,
    registerUserAnalyser,
    resetToIdle,
    dismissCallNotice,
    clearPlayback,
    sessionIdRef,
    teardownRtc,
    dispatchCallPhase,
  ]);

  const toggleCall = useCallback(async () => {
    // [FB34] Phase-aware, not isCalling-aware: isCalling is still false
    // throughout `connecting`, so branching on it here would take the
    // START branch on a second tap mid-connect and fire a SECOND
    // concurrent startCall. Every non-idle phase (connecting/live/failed)
    // routes to a clean stop instead — the failed-phase "Try again" chip is
    // a SEPARATE action (retryCall), not this toggle.
    if (callPhaseRef.current.phase !== 'idle') {
      stopCall();
    } else {
      await startCall();
    }
  }, [startCall, stopCall]);

  // [FB34] The ONE exported phase-aware stop for consumers OUTSIDE the call
  // surface itself (ChatContext's cleanupResources, the connection
  // machine's NOTIFY_TERMINAL/CONTACT_INFO_REQUESTED/HANDOFF_STARTED stops)
  // — isCallingRef alone would miss `connecting`/`failed` (isCalling is
  // still false there), so every EXTERNAL stop site must key on phase, or
  // e.g. "New chat" mid-connect, or a lost socket mid-connect, would leave
  // the half-built call running silently in the background. Returns
  // whether a call was actually active (and thus stopped), so callers that
  // branch on "was one running" (see useAssistantConnection.ts's
  // NOTIFY_TERMINAL/CONTACT_INFO_REQUESTED) don't need their own
  // isCallingRef-shaped guard duplicated alongside this.
  const stopCallIfActive = useCallback((): boolean => {
    if (callPhaseRef.current.phase === 'idle') return false;
    stopCall();
    return true;
  }, [stopCall]);

  // Mutes/unmutes the live mic by flipping each captured track's `enabled`
  // flag directly — cheaper and more immediate than tearing down/rebuilding
  // the AudioWorklet chain, and it doesn't touch the WS send loop (a muted
  // track just produces silence, so `workletNode.port.onmessage` keeps
  // sending silent frames, which is fine — server VAD simply sees no speech).
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      isMutedRef.current = next;
      callStreamRef.current?.getAudioTracks().forEach((track) => {
        track.enabled = !next;
      });
      return next;
    });
  }, []);

  // Manual mic switch (the dropdown escape hatch — Meet parity). A
  // USER-picked device that turns out silent gets a warning and nothing
  // else — auto-switching away would fight the user's explicit choice (and
  // nothing auto-switches devices anywhere anymore, FB28).
  const switchMic = useCallback(
    async (deviceId: string) => {
      if (!isCallingRef.current) return;
      const currentTrack = callStreamRef.current?.getAudioTracks()[0];
      if (currentTrack?.getSettings?.().deviceId === deviceId) return;
      const isRtc = transportRef.current === 'rtc';

      const myGeneration = callGenerationRef.current + 1;
      callGenerationRef.current = myGeneration;
      const aborted = () => callGenerationRef.current !== myGeneration;

      // [rtc-reliability] Reorder: teardownCapture used to stop the OLD
      // track before this point, unconditionally — leaving a brief
      // dead-track gap in the outgoing rtc audio (Sara "hears" silence)
      // for the whole rebuild (mic permission + worklet setup), not just
      // the swap itself. The analyser/worklet/context are still torn down
      // eagerly here (they don't feed the RTCRtpSender — see
      // teardownCapture's doc); only the TRACK's `.stop()` is deferred via
      // keepTrackAlive, and only for the rtc transport (the WS path has no
      // sender to keep fed, so it keeps the original eager-stop behavior —
      // no observable gap there).
      const oldTrack = teardownCapture({ keepTrackAlive: isRtc });
      try {
        const track = await buildCapture({
          deviceId,
          exactDevice: true,
          aborted,
          onSilent: () =>
            showCallNotice({
              kind: CallNoticeKind.WARNING,
              topic: CallNoticeTopic.MIC_SILENCE,
              title: "If you're speaking, Sara can't hear you.",
              subtitle: 'That microphone is picking up no sound.',
            }),
        });
        if (track) {
          if (isRtc) {
            // [rtc-spike] The normal (WS) path is done the instant the
            // fresh capture chain is live — the worklet pump just starts
            // reading the new track. The rtc transport ALSO needs the peer
            // connection's OUTGOING track swapped, or Sara keeps "hearing"
            // the old track. Match by kind with a first-sender fallback
            // rather than by identity — thrown failures (no sender, or
            // replaceTrack itself rejecting) fall into the catch block
            // below, same as any other switch failure.
            const pc = rtcPcRef.current;
            if (pc) {
              const sender =
                pc.getSenders().find((s) => s.track?.kind === 'audio') ??
                pc.getSenders()[0];
              if (!sender) {
                throw new Error('[rtc-spike] no RTCRtpSender to swap track on');
              }
              await sender.replaceTrack(track);
            }
            // Only NOW is it safe to retire the old track — the sender (if
            // any) is already on the new one.
            oldTrack?.getTracks().forEach((t) => t.stop());
          }
          // User intent is the ONLY thing that writes mic memory (FB28) —
          // a deliberately-picked mic is remembered across calls; nothing
          // automatic ever is.
          storePreferredMicId(deviceId);
          showCallNotice({
            kind: CallNoticeKind.SUCCESS,
            topic: CallNoticeTopic.MIC_SILENCE,
            title: track.label
              ? `Microphone switched to ${track.label}`
              : 'Microphone switched',
          });
        } else if (isRtc) {
          // Superseded mid-build (a stop/newer switch raced) — buildCapture
          // already tore ITS OWN (new) chain down internally. The old track
          // was kept alive for a swap that's no longer happening; nothing
          // else will stop it.
          oldTrack?.getTracks().forEach((t) => t.stop());
        }
      } catch (err) {
        const switchFailedNotice: CallNotice = {
          kind: CallNoticeKind.ERROR,
          topic: CallNoticeTopic.MIC_SILENCE,
          title: "Couldn't switch to that microphone.",
          subtitle: "You're still on your previous microphone.",
        };
        console.warn('[voice] mic switch failed:', err);
        showCallNotice(switchFailedNotice);
        // The swap never happened — release the old track now rather than
        // leaving it dangling.
        if (isRtc) oldTrack?.getTracks().forEach((t) => t.stop());
        // The failed attempt above may have left a partially-built chain
        // (context/stream created but never reached the 'running' gate)
        // assigned into the refs — release it before rebuilding, or the
        // rebuild's fresh AudioContext/stream would orphan it.
        teardownCapture();
        // Best-effort rebuild of the previous default chain so the call
        // isn't left with no capture at all after a failed switch. NOTE:
        // this rebuild going live clears the error notice above via
        // buildCapture's dismissCallNotice — re-show it after, so the user
        // still learns the switch failed.
        try {
          await buildCapture({
            aborted,
            onSilent: () =>
              showCallNotice({
                kind: CallNoticeKind.WARNING,
                topic: CallNoticeTopic.MIC_SILENCE,
                title: "Sara can't hear you.",
                subtitle:
                  'Your microphone is picking up no sound. If your laptop ' +
                  'lid is closed, open it or choose another microphone.',
              }),
          });
          showCallNotice(switchFailedNotice);
        } catch (rebuildErr) {
          console.warn(
            '[voice] mic switch fallback rebuild failed:',
            rebuildErr
          );
        }
      }
    },
    [buildCapture, teardownCapture, showCallNotice]
  );
  // Keep the nudge notice's escape hatch pointing at the CURRENT switchMic
  // (see the ref's declaration comment for why this indirection exists).
  switchMicRef.current = switchMic;

  // ── [rtc-reliability] WebRTC connection-drop recovery ───────────
  //
  // Nothing monitored the rtc peer connection after setup until now: a
  // network drop left the UI showing a live call forever, with no way back
  // short of the user hanging up and re-dialing manually. src/lib/
  // rtc-recovery.ts is the pure decision (grace window → one auto re-call →
  // recovered/dropped); everything below is the driver that owns the
  // timer/pc/fetch side effects that pure module's effects list requests.
  // [FB34] This machine arms only once `callPhase` reaches `live` — see
  // handleRtcConnectionStateChange's fan-out below. RTC-ONLY by
  // construction: `onConnectionStateChange` (wired in runStartAttempt/
  // performRtcRecall) only ever exists on an rtc pc, so a WS-transport call
  // — which never creates one — cannot reach any of this; FB28's existing
  // socket-drop notices (handleConnectionDrop/handleConnectionRestored,
  // below) are untouched.

  // Shared "this call is over, but you can start another" exit: stopCall's
  // normal cleanup (mic released, WS-side session detached, transcript
  // stays durable) + an ERROR toast with a "Call again" chip — a fresh
  // tap (real user gesture) that just calls startCall again. Used by the
  // DROPPED terminal state below AND the 60-minute session cap's hard end
  // (Commit 3) — same shape, different copy.
  //
  // A sonner toast, NOT a CallNotice: stopCall unmounts the call screen —
  // the only surface CallNotice renders into — so a notice shown after it
  // silently never appears (live-E2E confirmed: call ends, no feedback, no
  // chip anywhere in the DOM). Toasts live outside the call surface; this
  // matches the repo's precedent for out-of-call feedback (startCall's
  // PRE-call failures and text-mode rate-limit exhaustion both use sonner).
  const endCallWithRetryChip = useCallback(
    (title: string, subtitle: string) => {
      stopCall();
      toast.error(title, {
        description: subtitle,
        // A dropped call is a bigger deal than sonner's ~4s default gives
        // it — the user may look back at the screen late, and the toast IS
        // the only "Call again" affordance once the call surface unmounts.
        duration: 15_000,
        // startCallRef, NOT startCall directly — see that ref's declaration
        // comment for why a direct closure here would silently no-op.
        action: {
          label: 'Call again',
          onClick: () => void startCallRef.current(),
        },
      });
    },
    [stopCall]
  );
  // Keep the 60-minute cap's END timer (armed at ANSWER_RECEIVED time,
  // declared earlier in this file) pointing at the CURRENT
  // endCallWithRetryChip — same forward-reference idiom as
  // startCallRef/switchMicRef.
  endCallWithRetryChipRef.current = endCallWithRetryChip;

  // rtc-recovery's END_CALL effect — the re-call failed, or a second
  // failure spent the recall budget with nothing left to try.
  const runRtcDropped = useCallback(() => {
    endCallWithRetryChip(
      "Sara couldn't reconnect.",
      'The call ended, but nothing from your conversation was lost.'
    );
  }, [endCallWithRetryChip]);

  // rtc-recovery's START_RECALL effect — places a brand-new call on the
  // SAME live mic track/stream (OpenAI's Realtime API has no ICE-restart,
  // so recovery IS a new call, not a reconnect) and reports the outcome
  // back into the machine.
  const performRtcRecall = useCallback(async () => {
    const myGeneration = callGenerationRef.current;
    const track = callStreamRef.current?.getAudioTracks()[0];
    const stream = callStreamRef.current;
    if (!track || !stream) {
      // The capture chain is transport-agnostic and should still be live
      // whenever a recall fires (recovery only runs during a live rtc
      // call) — defensive fallback in case it somehow isn't.
      console.warn('[rtc-recovery] no live mic track to re-call with');
      dispatchRtcRecoveryRef.current({ type: 'RECALL_FAILED' });
      return;
    }
    try {
      const { pc: newPc, callId } = await startRtcCall({
        micTrack: track,
        micStream: stream,
        sessionId: sessionIdRef.current,
        onRemoteStream: attachRemoteStream,
        onConnectionStateChange: (state) =>
          handleRtcConnectionStateChangeRef.current(state),
        // [SDP-over-WS] see rtcWsSend's declaration comment above.
        wsSend: rtcWsSend(wsRef.current),
      });
      if (callGenerationRef.current !== myGeneration) {
        // The user hung up (or a wholly new call started) while this
        // re-call was in flight — this pc has no home to swap into. Close
        // it and tell the backend, same as any other hangup. The STALE
        // call's own id is passed so the backend hangs up ONLY this
        // orphaned call — a session-scoped hangup here would detach
        // whatever call is currently live, including one the user
        // legitimately started while we were in flight.
        newPc.onconnectionstatechange = null;
        void stopRtcCall(newPc, sessionIdRef.current, callId);
        return;
      }
      // Detach the OLD pc's listener before discarding it — a late
      // 'closed'/'failed' from the pc we just superseded must not be
      // mistaken by the reducer for a SECOND drop (see rtc-recovery.ts's
      // RECOVERED branch: it looks identical to a real one).
      const oldPc = rtcPcRef.current;
      if (oldPc) oldPc.onconnectionstatechange = null;
      oldPc?.close();
      rtcPcRef.current = newPc;
      rtcCallIdRef.current = callId;
      // The recovered call is a BRAND-NEW OpenAI call — minute zero on
      // OpenAI's clock — so the 60-minute cap's warn/end timers restart
      // here; left running they'd still fire relative to the ORIGINAL
      // call's start (a recall at minute 54 would warn at 55 and force-end
      // at 58, throwing away the fresh call's whole budget).
      armCallLimitTimers();
      console.info(`[rtc-recovery] re-call succeeded call_id=${callId}`);
      dispatchRtcRecoveryRef.current({ type: 'RECALL_SUCCEEDED' });
    } catch (err) {
      console.warn('[rtc-recovery] re-call failed:', err);
      dispatchRtcRecoveryRef.current({ type: 'RECALL_FAILED' });
    }
  }, [attachRemoteStream, sessionIdRef, armCallLimitTimers, wsRef]);

  // The ONE place useCallMode drives src/lib/rtc-recovery.ts's pure
  // machine: runs the reducer, executes whatever effects it returns, then
  // (except for a same-status no-op, and DROPPED — see below) shows the
  // notice rtcRecoveryPresentation projects for the new status.
  const dispatchRtcRecovery = useCallback(
    (event: RtcRecoveryEvent) => {
      const prevStatus = rtcRecoveryRef.current.status;
      const { state, effects } = rtcRecoveryReducer(
        rtcRecoveryRef.current,
        event
      );
      rtcRecoveryRef.current = state;

      for (const effect of effects) {
        switch (effect.type) {
          case 'START_GRACE_TIMER':
            if (rtcGraceTimerRef.current) {
              clearTimeout(rtcGraceTimerRef.current);
            }
            rtcGraceTimerRef.current = setTimeout(() => {
              rtcGraceTimerRef.current = null;
              dispatchRtcRecoveryRef.current({ type: 'GRACE_EXPIRED' });
            }, GRACE_MS);
            break;
          case 'CANCEL_GRACE_TIMER':
            if (rtcGraceTimerRef.current) {
              clearTimeout(rtcGraceTimerRef.current);
              rtcGraceTimerRef.current = null;
            }
            break;
          case 'START_RECALL':
            void performRtcRecall();
            break;
          case 'END_CALL':
            runRtcDropped();
            break;
        }
      }

      // Notice presentation is a pure function of status (rtc-recovery.ts's
      // rtcRecoveryPresentation) — EXCEPT DROPPED, whose ERROR notice needs
      // a live "Call again" onClick the pure helper can't carry;
      // runRtcDropped (the END_CALL effect above) already built and showed
      // that one. A same-status transition (e.g. a redundant 'connected'
      // while already idle) must leave whatever notice is currently showing
      // untouched — hence the prevStatus guard, not an unconditional
      // re-derive on every dispatch.
      if (state.status === prevStatus || state.status === 'dropped') return;
      const presentation = rtcRecoveryPresentation(state.status);
      if (presentation.kind === 'warning') {
        showCallNotice({
          kind: CallNoticeKind.WARNING,
          topic: CallNoticeTopic.CONNECTION,
          title: presentation.title,
          subtitle: presentation.subtitle,
        });
      } else if (presentation.kind === 'success') {
        showCallNotice({
          kind: CallNoticeKind.SUCCESS,
          topic: CallNoticeTopic.CONNECTION,
          title: presentation.title,
        });
      }
    },
    [performRtcRecall, runRtcDropped, showCallNotice]
  );
  dispatchRtcRecoveryRef.current = dispatchRtcRecovery;

  // The actual pc.onconnectionstatechange handler (see runStartAttempt/
  // performRtcRecall, which pass it in via handleRtcConnectionStateChangeRef
  // since they're declared earlier in this file). RTC-only by construction
  // (see this section's header).
  //
  // [FB34] Fans out by callPhase, not isCalling: PRE-live (callPhase still
  // `connecting`) a hard 'failed'/'closed' is the FIRST-EVER ICE result for
  // this attempt, not a drop from an established call — it becomes
  // START_FAILED(network) and burns none of rtc-recovery's one-recall
  // budget. POST-live (callPhase `live`), connection-health events flow to
  // rtc-recovery exactly as before FB34.
  const handleRtcConnectionStateChange = useCallback(
    (state: RTCPeerConnectionState) => {
      // Gated on PHASE, not isCallingRef: under the latency-overlap path
      // the pc can reach 'connected' (or 'failed') while the capture arm is
      // still pending — well before finalizeCallLive flips isCallingRef —
      // and an event dropped in that window would strand the phase machine
      // in `connecting` over a fully working call (PC_CONNECTED is only
      // ever fired once). 'idle'/'ending' means the attempt is already torn
      // down (or being torn down) — late events from a closed pc are noise.
      const phase = callPhaseRef.current.phase;
      if (
        transportRef.current !== 'rtc' ||
        phase === 'idle' ||
        phase === 'ending'
      )
        return;
      if (state === 'connected') {
        // [FB34] Only the FIRST connected event (pre-live) is this
        // attempt's own timing mark — a later 'connected' after rtc-
        // recovery's own re-call is a different call, not this one.
        if (phase !== 'live') {
          logVoiceTiming('connected');
        }
        dispatchCallPhase({ type: 'PC_CONNECTED' });
      }
      if (phase !== 'live') {
        // Pre-live, only a genuine ICE failure ('failed') matters. 'closed'
        // is deliberately ignored here: a pc only reaches 'closed' via our
        // own pc.close() (abort/stop choreography), and reacting to it
        // would let a self-inflicted close from e.g. the capture arm's
        // mic-denied cleanup overwrite the real failure reason with
        // 'network' before the choreography dispatches START_FAILED itself.
        // 'disconnected' pre-live is also deliberately a no-op: it's
        // spec-legal transient state that can self-heal, and browsers
        // escalate a genuinely dead connection to 'failed' on their own —
        // failing fast on it would false-positive; the user can always End
        // out of a stuck connect (toggleCall/stopCall are phase-aware).
        if (state === 'failed') {
          // Invalidate any still-pending start arm (the capture arm may be
          // mid-getUserMedia) so it aborts instead of resuming onto a dead
          // attempt.
          callGenerationRef.current += 1;
          // isCallingRef/isCalling may have already flipped true at
          // ANSWER_RECEIVED time (finalizeCallLive runs before ICE
          // necessarily finishes) — a failure here must reset them too, or
          // the pump-gating flag would keep claiming a call is active after
          // teardown.
          isCallingRef.current = false;
          setIsCalling(false);
          teardownCapture();
          teardownRtc();
          dispatchCallPhase({ type: 'START_FAILED', reason: 'network' });
        }
        return;
      }
      dispatchRtcRecovery({ type: 'CONNECTION_STATE', state });
    },
    [
      dispatchCallPhase,
      dispatchRtcRecovery,
      teardownCapture,
      teardownRtc,
      logVoiceTiming,
    ]
  );
  handleRtcConnectionStateChangeRef.current = handleRtcConnectionStateChange;

  // ── Connection-drop survival (FB28) ─────────────────────────────
  //
  // A live call no longer ends when the WebSocket drops: the capture chain
  // (mic, AudioContext, worklet) doesn't depend on the socket — the pump
  // already drops chunks while it's closed and resumes sending the moment
  // wsRef points at a reopened one, and the backend re-arms voice mode off
  // the first audio frame of the new session. Ending the call instead would
  // ALSO be unrecoverable-by-code on iOS, where rebuilding the audio chain
  // requires a fresh user gesture — a call that never ended just keeps
  // going. The connection layer (useAssistantConnection's onclose/onopen)
  // calls this pair; retry exhaustion is the one path that still ends the
  // call (see the NOTIFY_TERMINAL effect there).

  const handleConnectionDrop = useCallback(() => {
    if (!isCallingRef.current) return;
    connectionLostRef.current = true;
    showCallNotice({
      kind: CallNoticeKind.WARNING,
      topic: CallNoticeTopic.CONNECTION,
      title: 'Connection lost — reconnecting…',
      subtitle: "Hold on. Sara can't hear you until the connection is back.",
    });
  }, [showCallNotice]);

  const handleConnectionRestored = useCallback(() => {
    // Only announce a recovery the user was told about — a socket that
    // flapped without ever surfacing the drop notice stays silent.
    if (!isCallingRef.current || !connectionLostRef.current) return;
    connectionLostRef.current = false;
    showCallNotice({
      kind: CallNoticeKind.SUCCESS,
      topic: CallNoticeTopic.CONNECTION,
      title: "You're back online — go ahead.",
    });
  }, [showCallNotice]);

  // ── Turn 15 · rate-limit waiting UX (supersedes the FB32 stopgap) ──
  //
  // The backend emits rate_limit_wait alongside its normal turn_state
  // "thinking" frame when OpenAI rate-limits a response and schedules (or
  // exhausts) a delayed retry — the orb shimmer / "Working…" timeline
  // already stays alive from that frame. Only shown in-call, same
  // restriction as the notices above: TEXT mode's "Working…" timeline
  // already covers the wait, and there's no call surface there to host a
  // CallNotice (see useAssistantConnection.ts's RATE_LIMIT_WAIT case).
  //
  // `modelBusy` is ORTHOGONAL to `callNotice`/vizMode — it drives the orb's
  // dim-and-rest treatment (call-orb.tsx's `dimmed` prop) and the status
  // cluster's "Resting a moment" override (call-status-cluster.tsx's
  // `resting` prop), both of which must hold their EXHAUSTED look no matter
  // what turn_state the backend happens to be sending underneath.
  const [modelBusy, setModelBusy] = useState<ModelBusyState | null>(null);

  // "The exhale" (15e) — a MODEL_BUSY notice retracting because Sara
  // actually responded doesn't just vanish: CallScreen animates it out
  // (translateY + fade) and the orb plays one extra one-shot ripple on top
  // of its normal AGENT-entry bloom. Both need to survive the render where
  // `callNotice`/`modelBusy` already read "cleared", so a snapshot (and a
  // bumped signal for the orb) live one timer's worth longer — see
  // handleModelResponded below. NOT used by any other retraction path
  // (dismiss, call end, other topics): those still clear `callNotice`
  // directly and instantly, exactly as before Turn 15.
  const [releasingNotice, setReleasingNotice] = useState<CallNotice | null>(
    null
  );
  const [orbReleaseSignal, setOrbReleaseSignal] = useState(0);
  const releaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Turn 15d's "Ask again" chip — the manual-retry escape hatch out of
  // EXHAUSTED. Sends the FIXED client→server RETRY_RESPONSE frame directly
  // on the socket — same "bypasses the outbox" precedent as
  // sendEndHandoff/submitContactInfo in ChatContext.tsx (a one-shot control
  // frame with no ack/retry lifecycle to attach to a Message). useCallMode
  // already owns wsRef (the capture pump sends audio the same way), so no
  // new plumbing into ChatContext is needed for the send side.
  const askAgain = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: WSEventType.RETRY_RESPONSE }));
    }
    // A retry is back in flight the instant the user asks — swap straight
    // to the calm waiting treatment. Direct assignment, not the
    // backend-driven recovery below: there's no fresh rate_limit_wait frame
    // to derive it from, and no exit animation either (superseding the
    // current notice has always been instant here — see showCallNotice).
    setModelBusy('waiting');
    showCallNotice(patienceNotice());
  }, [wsRef, showCallNotice]);

  const handleModelBusy = useCallback(
    (retryIn: number | null) => {
      if (!isCallingRef.current) return;
      setModelBusy(nextModelBusyState(retryIn));
      showCallNotice(modelBusyNotice(retryIn, askAgain));
    },
    [showCallNotice, askAgain]
  );

  // Retracts a stale MODEL_BUSY notice the moment the backend reports agent
  // output has actually started (turn_state: responding — see the call site
  // in useAssistantConnection.ts). retractModelBusyNotice's existing
  // topic-scoped check doubles as the "was there actually something to
  // recover from" test: a no-op (returns the same reference) when nothing
  // is showing on that topic, or when it's some OTHER topic — every normal
  // turn reaches "responding", not just a rate-limited one, so this fires
  // constantly with nothing to do.
  //
  // Plain sequential code, not a setCallNoticeState((prev) => …) functional
  // updater: an updater must be a PURE function of its previous state (React
  // Strict Mode double-invokes updaters specifically to catch impurity), but
  // the old version fired setReleasingNotice/setOrbReleaseSignal/
  // clearTimeout/setTimeout from inside one — a second, StrictMode-only
  // invocation would double those side effects. Reading callNoticeRef.current
  // instead of the updater's `prev` sidesteps that entirely; the "the exhale"
  // snapshot surviving the state clear (see releasingNotice's declaration
  // comment above) is otherwise unchanged.
  const handleModelResponded = useCallback(() => {
    setModelBusy(null);
    const prev = callNoticeRef.current;
    if (retractModelBusyNotice(prev) === prev) return; // nothing to release
    if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
    setReleasingNotice(prev);
    setOrbReleaseSignal((n) => n + 1);
    releaseTimerRef.current = setTimeout(() => {
      releaseTimerRef.current = null;
      setReleasingNotice(null);
    }, NOTICE_RELEASE_MS);
    setCallNoticeState(null);
  }, []);

  return {
    isCalling,
    isCallingRef,
    toggleCall,
    startCall,
    stopCall,
    stopCallIfActive,
    // [FB34] External-facing call-start phase — see call-phase.ts's header
    // for why this (not isCalling) is the axis every consumer outside this
    // hook should gate on.
    callPhase: callPhaseState.phase,
    callFailureReason: callPhaseState.reason,
    waitingOnMicPermission,
    retryCall,
    showCC,
    setShowCC,
    isMuted,
    toggleMute,
    callStartedAtRef,
    audioInputs,
    audioDevices,
    activeMicId,
    switchMic,
    callNotice,
    dismissCallNotice,
    handleConnectionDrop,
    handleConnectionRestored,
    modelBusy,
    releasingNotice,
    orbReleaseSignal,
    handleModelBusy,
    handleModelResponded,
  };
}

export type { CallFailureReason, CallPhase };
